<template>
  <div class="market-subscription">
    <PageHeader
      title="마켓 구독"
      description="추적할 마켓정보를 입력하세요."
      :margin-bottom="true"
    />

    <div class="toolbar">
      <div class="filter-item url">
        <label class="filter-label">마켓 URL</label>
        <el-input
          v-model="filters.marketUrl"
          placeholder="https://smartstore.naver.com/example"
          clearable
          @keyup.enter="handleSearch"
        />
      </div>
      <div class="filter-item">
        <label class="filter-label">그룹</label>
        <el-select
          v-model="filters.groupid"
          placeholder="전체"
          clearable
        >
          <el-option
            v-for="group in groupOptions"
            :key="group.groupid"
            :label="groupLabel(group)"
            :value="group.groupid"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <label class="filter-label">상태</label>
        <el-select
          v-model="filters.status"
          placeholder="전체"
          clearable
        >
          <el-option
            v-for="status in marketStatuses"
            :key="status"
            :label="status"
            :value="status"
          />
        </el-select>
      </div>
      <div class="toolbar-spacer" />
      <div class="toolbar-actions">
        <el-button type="primary" :icon="Search" @click="handleSearch">
          검색
        </el-button>
        <el-button plain @click="cycleSortMode">
          {{ sortModeLabel }}
        </el-button>
        <el-button plain @click="openGroupModal">
          그룹 수정/추가
        </el-button>
        <el-button plain @click="openMarketModal">
          마켓 추가/변경
        </el-button>
        <el-button
          :disabled="selectedMarkets.length === 0"
          @click="openBulkModal"
        >
          일괄수정 ({{ selectedMarkets.length }})
        </el-button>
      </div>
    </div>

    <div class="list-card">
      <AppLoading v-if="loading" text="마켓 정보를 불러오는 중입니다..." />
      <template v-else>
        <el-table
          ref="tableRef"
          :data="markets"
          border
          style="width: 100%"
          class="data-table"
          @selection-change="handleSelectionChange"
          :empty-text="emptyText"
        >
          <el-table-column type="selection" width="40" />
          <el-table-column prop="groupid" label="그룹" width="70">
            <template #default="{ row }">
              <span class="group-cell">{{ row.groupid ?? '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="마켓 URL" min-width="220">
            <template #default="{ row }">
              <div class="url-cell">
                <span class="url-text" :title="row.marketUrl">{{ row.marketUrl }}</span>
                <el-icon
                  class="url-open-icon"
                  @click.stop="openMarketUrl(row.marketUrl)"
                >
                  <Link />
                </el-icon>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="productCount" label="상품 수" min-width="70" align="right">
            <template #default="{ row }">{{ formatNumber(row.productCount) }}</template>
          </el-table-column>
          <el-table-column
            prop="interestedCustomerCount"
            label="관심 고객"
            min-width="85"
            align="right"
          >
            <template #default="{ row }">
              {{ formatNumber(row.interestedCustomerCount) }}
            </template>
          </el-table-column>
          <el-table-column prop="bestCount" label="BEST 수" min-width="80" align="right">
            <template #default="{ row }">{{ formatNumber(row.bestCount) }}</template>
          </el-table-column>
          <el-table-column prop="totalCollected" label="누적 수집" min-width="85" align="right">
            <template #default="{ row }">{{ formatNumber(row.totalCollected) }}</template>
          </el-table-column>
          <el-table-column label="등록율" min-width="90" align="right">
            <template #default="{ row }">
              <span :class="['registration-rate', registrationRateClass(row)]">
                {{ formatRegistrationRate(row) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="updatedAt" label="업데이트일" min-width="110" align="center">
            <template #default="{ row }">{{ formatDateOnly(row.updatedAt) }}</template>
          </el-table-column>
          <el-table-column prop="createdAt" label="등록일" min-width="110" align="center">
            <template #default="{ row }">{{ formatDateOnly(row.createdAt) }}</template>
          </el-table-column>
          <el-table-column prop="status" label="상태" min-width="80" align="center">
            <template #default="{ row }">
              <span :class="['status-text', statusColorClass(row.status)]">{{ row.status || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="잠금" min-width="55" align="center">
            <template #default="{ row }">
              <el-button
                size="small"
                circle
                plain
                class="lock-button"
                :disabled="isLockLoading(row.marketid)"
                :type="row.locked ? 'primary' : 'info'"
                @click="toggleMarketLock(row)"
              >
                <el-icon v-if="row.locked">
                  <Lock />
                </el-icon>
                <el-icon v-else>
                  <Unlock />
                </el-icon>
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="삭제" width="70" fixed="right" header-align="center">
            <template #default="{ row }">
              <div class="table-actions">
                <template v-if="['연결실패', '연결중지', '판매중지'].includes(row.status)">
                  <el-button
                    size="small"
                    :loading="retryingMarketId === row.marketid"
                    @click="handleRetryMarket(row)"
                  >
                    동기화
                  </el-button>
                </template>
                <el-button v-else size="small" @click="handleDelete(row)">
                  삭제
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination" v-if="pagination.total > 0">
          <el-pagination
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="[20, 50, 100, 250, 500]"
            :total="pagination.total"
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.limit"
            @current-change="handlePageChange"
            @size-change="handleSizeChange"
          />
        </div>
      </template>
    </div>

    <el-dialog
      v-model="groupModal.visible"
      title="그룹 수정"
      width="640px"
      align-center
    >
      <AppLoading
        v-if="groupModal.loading"
        text="그룹 정보를 불러오는 중입니다..."
      />
      <template v-else>
        <div class="group-create-row">
          <el-input
            v-model="groupModal.newGroupName"
            placeholder="새 그룹명을 입력하세요"
            maxlength="30"
            @keyup.enter="handleGroupCreate"
          />
          <el-button
            plain
            :loading="groupModal.creating"
            @click="handleGroupCreate"
          >
            그룹 추가
          </el-button>
        </div>
        <div v-if="groupModal.rows.length" class="group-edit-list">
          <div class="group-edit-header">
            <span>그룹 ID</span>
            <span>그룹명</span>
            <span>동기화</span>
            <span>관리</span>
          </div>
          <div
            class="group-edit-row"
            v-for="row in groupModal.rows"
            :key="row.groupid"
          >
            <span class="group-row-id">{{ row.groupid }}</span>
            <div class="group-row-name">
              <template v-if="row.editing">
                <el-input
                  v-model="row.groupname"
                  class="group-row-input"
                  placeholder="그룹명을 입력하세요"
                  maxlength="30"
                  :disabled="isGroupRowSaving(row.groupid)"
                  @keyup.enter="handleGroupNameSubmit(row)"
                  @blur="handleGroupNameBlur(row)"
                />
              </template>
              <template v-else>
                <span class="group-row-text">{{ row.groupname }}</span>
                <el-icon
                  class="group-edit-icon"
                  @click="activateGroupNameEdit(row)"
                >
                  <Edit />
                </el-icon>
              </template>
            </div>
            <div class="group-row-switch">
              <el-switch
                v-model="row.syncEnabled"
                :loading="isGroupRowSaving(row.groupid)"
                :disabled="isGroupRowSaving(row.groupid)"
                @change="value => handleGroupSyncToggle(row, value)"
              />
              <span class="switch-label">{{ row.syncEnabled ? '사용' : '중지' }}</span>
            </div>
            <div class="group-row-actions">
              <el-button
                type="danger"
                link
                size="small"
                :disabled="isGroupRowSaving(row.groupid)"
                @click="() => handleDeleteGroup(row)"
              >
                삭제
              </el-button>
            </div>
          </div>
        </div>
        <p v-else class="group-empty-text">등록된 그룹이 없습니다.</p>
      </template>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeGroupModal">닫기</el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog
      v-model="marketModal.visible"
      title="마켓 추가/변경"
      width="780px"
      align-center
    >
      <el-form label-position="top" class="market-form">
        <div class="market-form-top">
          <el-form-item label="그룹 선택" class="market-form-group">
            <el-select
              v-model="marketModal.form.groupid"
              placeholder="선택 안 함"
              clearable
            >
              <el-option
                v-for="group in groupOptions"
                :key="group.groupid"
                :label="group.groupname"
                :value="group.groupid"
              />
            </el-select>
          </el-form-item>
        </div>
        <el-form-item label="마켓 URL 목록" required class="market-form-textarea">
          <el-input
            v-model="marketModal.form.marketUrlsText"
            type="textarea"
            :rows="16"
            placeholder="마켓 URL을 한 줄에 하나씩 입력하세요."
          />
        </el-form-item>
        <p class="helper-text">* 이미 등록된 URL은 최신 정보로 업데이트됩니다.</p>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="closeMarketModal">취소</el-button>
          <el-button
            type="primary"
            :loading="marketModal.submitting"
            @click="handleSubmitMarketModal"
          >
            저장
          </el-button>
        </span>
      </template>
    </el-dialog>

    <el-dialog
      v-model="bulkModal.visible"
      title="일괄수정"
      width="520px"
      align-center
    >
      <p class="bulk-info-text">선택된 마켓: {{ selectedMarkets.length }}개</p>
      <div class="bulk-actions-grid">
        <div class="bulk-actions-line">
          <el-button
            :loading="bulkModal.loading"
            :disabled="selectedMarkets.length === 0"
            @click="handleBulkLock(true)"
          >
            일괄 잠금
          </el-button>
          <el-button
            :loading="bulkModal.loading"
            :disabled="selectedMarkets.length === 0"
            @click="handleBulkLock(false)"
          >
            일괄 잠금해제
          </el-button>
        </div>
        <div class="bulk-actions-line">
          <el-button
            plain
            :disabled="bulkModal.loading || selectedMarkets.length === 0 || syncableCount === 0"
            :loading="bulkModal.loading"
            @click="handleBulkRetrySync"
          >
            일괄 동기화 ({{ syncableCount }})
          </el-button>
          <el-button
            plain
            :disabled="bulkModal.loading || selectedMarkets.length === 0"
            @click="handleBulkOpenTabs"
          >
            탭열기
          </el-button>
          <el-button
            :loading="bulkModal.loading"
            :disabled="selectedMarkets.length === 0"
            @click="handleBulkDelete(false)"
          >
            일반삭제
          </el-button>
          <el-button
            type="danger"
            :loading="bulkModal.loading"
            :disabled="selectedMarkets.length === 0"
            @click="handleBulkDelete(true)"
          >
            영구삭제
          </el-button>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button :disabled="bulkModal.loading" @click="closeBulkModal">닫기</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Edit, Lock, Unlock, Link } from '@element-plus/icons-vue';
import AppLoading from '../../components/common/loading.vue';
import PageHeader from '@/components/common/header.vue';
import {
  fetchGroupCodes,
  createGroupCode,
  updateGroupCode,
  deleteGroupCode,
  retryFailedMarket,
  fetchMarketList,
  saveMarketList,
  deleteMarkets
} from '@/services/n2n';

export default {
  name: 'N2nMarketSubscription',
  components: {
    AppLoading,
    PageHeader
  },
  setup() {
    const loading = ref(false);
    const markets = ref([]);
    const groupOptions = ref([]);
    const selectedMarkets = ref([]);
    const tableRef = ref(null);
    const retryingMarketId = ref(null);
    const lockLoading = reactive({});
    const filters = reactive({
      marketUrl: '',
      groupid: '',
      status: ''
    });
    const pagination = reactive({
      page: 1,
      limit: 50,
      total: 0
    });
    const sortModes = ['default', 'product_count', 'interested', 'best', 'collected', 'upload_success', 'deleted'];
    const sortMode = ref('default');
    const sortModeLabels = {
      default: '기본순서',
      product_count: '상품 개수순',
      interested: '관심 고객순',
      best: 'BEST 순',
      collected: '누적수집순',
      upload_success: '등록성공순',
      deleted: '삭제순'
    };

    let autoSearchTimer = null;
    let autoSearchSuspended = false;

    const scheduleSearch = (delay = 0) => {
      if (autoSearchSuspended) return;
      if (autoSearchTimer) {
        clearTimeout(autoSearchTimer);
      }
      autoSearchTimer = setTimeout(() => {
        pagination.page = 1;
        loadMarkets();
      }, delay);
    };

    const marketStatuses = ['연결중', '연결성공', '연결실패', '연결중지', '판매중지'];

    const marketModal = reactive({
      visible: false,
      submitting: false,
      form: {
        groupid: '',
        marketUrlsText: ''
      }
    });

    const bulkModal = reactive({
      visible: false,
      loading: false
    });

    const syncableStatuses = new Set(['연결실패', '연결중지', '판매중지']);
    const syncableMarkets = computed(() =>
      (selectedMarkets.value || []).filter(row => row?.marketid && syncableStatuses.has(row?.status))
    );
    const syncableCount = computed(() => syncableMarkets.value.length);

    const groupModal = reactive({
      visible: false,
      loading: false,
      rows: [],
      saving: {},
      newGroupName: '',
      creating: false
    });

    const emptyText = computed(() => (loading.value ? '로딩 중...' : '등록된 마켓이 없습니다.'));
    const sortModeLabel = computed(() => sortModeLabels[sortMode.value] || '기본');

    const findGroupOptionById = groupid =>
      groupOptions.value.find(group => String(group.groupid) === String(groupid));

    const populateGroupModalRows = () => {
      const rows = groupOptions.value
        .filter(group => group?.groupid !== undefined && group?.groupid !== null)
        .map(group => {
          const groupname = group.groupname ?? '';
          const syncEnabled = Boolean(group.syncEnabled);
          return {
            groupid: group.groupid,
            groupname,
            syncEnabled,
            originalName: groupname,
            originalSync: syncEnabled,
            editing: false
          };
        });
      groupModal.rows = rows;
    };

    const syncGroupModalRows = () => {
      if (!groupModal.visible) return;
      populateGroupModalRows();
    };

    const applyGroupNameLocally = (groupid, name) => {
      const target = findGroupOptionById(groupid);
      if (target) {
        target.groupname = name;
      }
    };

    const applyGroupSyncLocally = (groupid, value) => {
      const target = findGroupOptionById(groupid);
      if (target) {
        target.syncEnabled = value;
      }
    };

    const groupLabel = group => {
      if (!group) return '-';
      const id = group.groupid ?? '';
      const name = group.groupname ?? '';
      return id && name ? `${id} - ${name}` : id || name || '-';
    };

    const statusColorClass = status => {
      if (status === '연결성공') return 'status-success';
      if (status === '연결실패') return 'status-error';
      if (status === '연결중지' || status === '판매중지') return 'status-warning';
      return '';
    };

    const formatNumber = value => {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return num.toLocaleString();
    };

    const formatRegistrationRate = row => {
      const uploadSuccessCount = Number(row?.uploadSuccessCount || 0);
      const deletedCount = Number(row?.deletedCount || 0);
      const total = uploadSuccessCount + deletedCount;
      if (total <= 0) return '0%(0/0)';
      const rate = Math.round((uploadSuccessCount / total) * 100);
      return `${rate}%(${formatNumber(uploadSuccessCount)}/${formatNumber(total)})`;
    };

    const registrationRateClass = row => {
      const uploadSuccessCount = Number(row?.uploadSuccessCount || 0);
      const deletedCount = Number(row?.deletedCount || 0);
      const total = uploadSuccessCount + deletedCount;
      if (total <= 0) return '';
      const rate = Math.round((uploadSuccessCount / total) * 100);
      if (rate === 0) return '';
      if (rate <= 30) return 'rate-danger';
      if (rate >= 60) return 'rate-success';
      return 'rate-warning';
    };

    const formatDateOnly = value => {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const cycleSortMode = () => {
      const currentIndex = sortModes.indexOf(sortMode.value);
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % sortModes.length : 0;
      sortMode.value = sortModes[nextIndex];
    };

    const openMarketUrl = url => {
      if (!url) return;
      const targetUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
      return window.open(targetUrl, '_blank', 'noopener');
    };

    const loadGroups = async () => {
      try {
        const { data } = await fetchGroupCodes();
        if (data?.data) {
          groupOptions.value = data.data;
        } else if (Array.isArray(data)) {
          groupOptions.value = data;
        } else {
          groupOptions.value = data?.data ?? [];
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '그룹 정보를 불러오지 못했습니다.');
      } finally {
        syncGroupModalRows();
      }
    };

    const buildQueryParams = () => {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (filters.marketUrl) params.market_url = filters.marketUrl;
      if (filters.groupid) params.groupid = filters.groupid;
      if (filters.status) params.status = filters.status;
      if (sortMode.value && sortMode.value !== 'default') {
        params.order_by = sortMode.value;
      }
      return params;
    };

    const loadMarkets = async () => {
      loading.value = true;
      try {
        const params = buildQueryParams();
        const { data } = await fetchMarketList(params);
        if (Array.isArray(data)) {
          markets.value = data;
          pagination.total = data.length;
        } else {
          const items = data?.data || data?.items || [];
          markets.value = items;
          const paginationData = data?.pagination;
          if (paginationData) {
            pagination.total = paginationData.total ?? 0;
          } else {
            pagination.total = items.length;
          }
        }
      } catch (error) {
        markets.value = [];
        pagination.total = 0;
        ElMessage.error(error.response?.data?.message || '마켓 정보를 불러오지 못했습니다.');
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (autoSearchTimer) {
        clearTimeout(autoSearchTimer);
      }
      pagination.page = 1;
      loadMarkets();
    };

    const handlePageChange = page => {
      pagination.page = page;
      loadMarkets();
    };

    const handleSizeChange = size => {
      pagination.limit = size;
      pagination.page = 1;
      loadMarkets();
    };

    const handleSelectionChange = selection => {
      selectedMarkets.value = selection;
    };

    const openBulkModal = () => {
      bulkModal.visible = true;
    };

    const closeBulkModal = () => {
      bulkModal.visible = false;
      bulkModal.loading = false;
    };

    const clearSelection = () => {
      selectedMarkets.value = [];
      tableRef.value?.clearSelection?.();
    };

    const handleDelete = async row => {
      let action = 'confirm';
      try {
        await ElMessageBox.confirm(
          '해당 마켓을 어떻게 삭제할까요?',
          '삭제 확인',
          {
            confirmButtonText: '영구 삭제',
            cancelButtonText: '일반 삭제',
            type: 'warning',
            distinguishCancelAndClose: true,
            showClose: true
          }
        );
      } catch (error) {
        action = error;
      }
      if (action === 'close') {
        return;
      }
      const permanent = action === 'confirm';
      try {
        await deleteMarkets([row.marketid], { permanent });
        ElMessage.success(permanent ? '마켓을 영구 삭제했습니다.' : '마켓을 삭제했습니다.');
        loadMarkets();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '삭제에 실패했습니다.');
      }
    };

    const handleBulkDelete = async permanent => {
      if (bulkModal.loading) return;
      if (!selectedMarkets.value.length) return;
      const count = selectedMarkets.value.length;
      const title = permanent ? '영구삭제' : '일반삭제';
      const message = permanent
        ? `${count}개 마켓을 영구 삭제할까요? (복구 불가)`
        : `${count}개 마켓을 일반 삭제할까요?`;
      try {
        await ElMessageBox.confirm(message, title, {
          confirmButtonText: '삭제',
          cancelButtonText: '취소',
          type: 'warning',
          showClose: true
        });
      } catch {
        return;
      }

      bulkModal.loading = true;
      try {
        const ids = selectedMarkets.value.map(item => item.marketid);
        await deleteMarkets(ids, { permanent });
        ElMessage.success(permanent ? '선택한 마켓을 영구 삭제했습니다.' : '선택한 마켓을 삭제했습니다.');
        clearSelection();
        await loadMarkets();
        closeBulkModal();
      } catch (err) {
        ElMessage.error(err.response?.data?.message || '일괄 삭제에 실패했습니다.');
      } finally {
        bulkModal.loading = false;
      }
    };

    const handleBulkOpenTabs = () => {
      if (bulkModal.loading) return;
      if (!selectedMarkets.value.length) return;

      const urls = [
        ...new Set(
          selectedMarkets.value
            .map(item => item?.marketUrl)
            .filter(Boolean)
        )
      ];
      for (const url of urls) {
        openMarketUrl(url);
      }

    };

    const runWithConcurrency = async (items, concurrency, worker) => {
      const results = new Array(items.length);
      let cursor = 0;
      const limit = Math.max(1, Math.min(concurrency || 1, items.length));

      const runners = Array.from({ length: limit }, async () => {
        while (cursor < items.length) {
          const index = cursor;
          cursor += 1;
          try {
            await worker(items[index]);
            results[index] = { status: 'fulfilled' };
          } catch (error) {
            results[index] = { status: 'rejected', reason: error };
          }
        }
      });

      await Promise.all(runners);
      return results;
    };

    const handleBulkRetrySync = async () => {
      if (bulkModal.loading) return;
      const targets = syncableMarkets.value;
      if (!targets.length) {
        ElMessage.warning('동기화할 수 있는 마켓이 없습니다. (연결실패/연결중지/판매중지)');
        return;
      }

      try {
        await ElMessageBox.confirm(
          `${targets.length}개 마켓 동기화를 요청할까요?`,
          '일괄 동기화',
          {
            confirmButtonText: '요청',
            cancelButtonText: '취소',
            type: 'warning',
            showClose: true
          }
        );
      } catch {
        return;
      }

      bulkModal.loading = true;
      try {
        const ids = targets.map(row => row.marketid);
        const results = await runWithConcurrency(ids, 20, id => retryFailedMarket(id));
        const success = results.filter(r => r?.status === 'fulfilled').length;
        const failed = results.length - success;

        if (success > 0 && failed === 0) {
          ElMessage.success(`${success}개 마켓 동기화를 요청했습니다.`);
        } else if (success > 0) {
          ElMessage.warning(`${success}개 성공, ${failed}개 실패했습니다.`);
        } else {
          ElMessage.error('동기화 요청에 실패했습니다.');
        }

        clearSelection();
        await loadMarkets();
        closeBulkModal();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '일괄 동기화 요청에 실패했습니다.');
      } finally {
        bulkModal.loading = false;
      }
    };

    const groupSelectedByGroupid = rows => {
      const map = new Map();
      (rows || []).forEach(row => {
        const marketUrl = row?.marketUrl;
        if (!marketUrl) return;
        const groupid = row?.groupid || '';
        if (!map.has(groupid)) map.set(groupid, []);
        map.get(groupid).push(marketUrl);
      });
      return [...map.entries()].map(([groupid, marketUrls]) => ({
        groupid,
        marketUrls
      }));
    };

    const handleBulkLock = async locked => {
      if (bulkModal.loading) return;
      if (!selectedMarkets.value.length) return;

      bulkModal.loading = true;
      try {
        const targets =
          locked === true
            ? selectedMarkets.value.filter(row => !row?.locked)
            : selectedMarkets.value;
        const groups = groupSelectedByGroupid(targets);
        if (!groups.length) {
          ElMessage.warning(locked ? '이미 잠금된 마켓은 제외되어 처리할 항목이 없습니다.' : '잠금 처리할 마켓이 없습니다.');
          return;
        }
        for (const { groupid, marketUrls } of groups) {
          const payload = buildMarketPayload(groupid, marketUrls, locked);
          await saveMarketList(payload);
        }
        ElMessage.success(locked ? '선택한 마켓을 일괄 잠금 처리했습니다.' : '선택한 마켓을 일괄 잠금해제 처리했습니다.');
        clearSelection();
        await loadMarkets();
        closeBulkModal();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '일괄 잠금 처리에 실패했습니다.');
      } finally {
        bulkModal.loading = false;
      }
    };

    const openMarketModal = () => {
      marketModal.visible = true;
      if (!groupOptions.value.length) {
        loadGroups();
      }
    };

    const closeMarketModal = () => {
      marketModal.visible = false;
      marketModal.form.groupid = '';
      marketModal.form.marketUrlsText = '';
    };

    const openGroupModal = async () => {
      groupModal.visible = true;
      groupModal.newGroupName = '';
      groupModal.loading = true;
      try {
        if (!groupOptions.value.length) {
          await loadGroups();
        } else {
          populateGroupModalRows();
        }
      } finally {
        groupModal.loading = false;
      }
    };

    const closeGroupModal = () => {
      groupModal.visible = false;
      groupModal.rows = [];
      groupModal.saving = {};
      groupModal.newGroupName = '';
      groupModal.creating = false;
      groupModal.loading = false;
    };

    const isGroupRowSaving = groupid => Boolean(groupModal.saving[groupid]);

    const submitGroupUpdate = async (groupid, payload, successMessage) => {
      if (!groupid) return false;
      groupModal.saving[groupid] = true;
      try {
        await updateGroupCode(groupid, payload);
        if (successMessage) {
          ElMessage.success(successMessage);
        }
        return true;
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '그룹 수정에 실패했습니다.');
        return false;
      } finally {
        groupModal.saving[groupid] = false;
      }
    };

    const handleGroupCreate = async () => {
      if (groupModal.creating) return;
      const name = groupModal.newGroupName.trim();
      if (!name) {
        ElMessage.warning('새 그룹명을 입력하세요.');
        return;
      }
      groupModal.creating = true;
      try {
        await createGroupCode({ groupname: name });
        ElMessage.success('새 그룹이 추가되었습니다.');
        groupModal.newGroupName = '';
        await loadGroups();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '그룹 추가에 실패했습니다.');
      } finally {
        groupModal.creating = false;
      }
    };

    const activateGroupNameEdit = row => {
      if (!row) return;
      groupModal.rows.forEach(item => {
        if (item !== row) {
          item.editing = false;
          item.groupname = item.originalName;
        }
      });
      row.editing = true;
    };

    const handleGroupNameBlur = row => {
      if (!row || !row.editing) return;
      const trimmed = row.groupname.trim();
      if (!trimmed) {
        row.groupname = row.originalName;
        row.editing = false;
        return;
      }
      if (trimmed === row.originalName) {
        row.editing = false;
        return;
      }
      handleGroupNameSubmit(row);
    };

    const handleGroupNameSubmit = async row => {
      if (!row) return;
      if (isGroupRowSaving(row.groupid)) return;
      const trimmed = row.groupname.trim();
      if (!trimmed) {
        ElMessage.warning('그룹명을 입력하세요.');
        row.groupname = row.originalName;
        return;
      }
      if (trimmed === row.originalName) {
        row.editing = false;
        row.groupname = row.originalName;
        return;
      }
      const success = await submitGroupUpdate(row.groupid, { groupname: trimmed }, '그룹명이 변경되었습니다.');
      if (success) {
        row.originalName = trimmed;
        row.groupname = trimmed;
        applyGroupNameLocally(row.groupid, trimmed);
        row.editing = false;
      } else {
        row.groupname = row.originalName;
      }
    };

    const handleGroupSyncToggle = async (row, value) => {
      if (!row) return;
      if (isGroupRowSaving(row.groupid)) return;
      const previous = row.originalSync;
      if (value === previous) return;
      const success = await submitGroupUpdate(
        row.groupid,
        { sync: value },
        '동기화 여부가 변경되었습니다.'
      );
      if (success) {
        row.originalSync = value;
        row.syncEnabled = value;
        applyGroupSyncLocally(row.groupid, value);
      } else {
        row.syncEnabled = previous;
      }
    };

    const handleDeleteGroup = async row => {
      if (!row?.groupid) return;
      if (isGroupRowSaving(row.groupid)) return;
      try {
        await ElMessageBox.confirm(
          `그룹 ${row.groupname} 을 삭제하면 연결된 마켓도 함께 삭제됩니다.`,
          '그룹 삭제',
          {
            confirmButtonText: '삭제',
            cancelButtonText: '취소',
            type: 'warning'
          }
        );
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.response?.data?.message || '삭제 요청이 취소되었습니다.');
        }
        return;
      }

      groupModal.saving[row.groupid] = true;
      try {
        const { data } = await deleteGroupCode(row.groupid);
        const deletedMarkets = data?.deletedMarkets;
        if (typeof deletedMarkets === 'number') {
          ElMessage.success(`그룹과 ${deletedMarkets}개 마켓을 삭제했습니다.`);
        } else {
          ElMessage.success('그룹을 삭제했습니다.');
        }
        await loadGroups();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '그룹 삭제에 실패했습니다.');
      } finally {
        groupModal.saving[row.groupid] = false;
      }
    };

    const handleRetryMarket = async row => {
      if (!row?.marketid || retryingMarketId.value) return;
      retryingMarketId.value = row.marketid;
      try {
        await retryFailedMarket(row.marketid);
        ElMessage.success('동기화를 요청했습니다.');
        await loadMarkets();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '동기화에 실패했습니다.');
      } finally {
        retryingMarketId.value = null;
      }
    };

    const buildMarketPayload = (groupid, urls, locked) => {
      const payload = {
        marketUrls: urls
      };
      if (groupid) {
        payload.groupid = groupid;
      }
      if (typeof locked === 'boolean') {
        payload.locked = locked;
      }
      return payload;
    };

    const updateMarketRow = (marketid, updater) => {
      if (!marketid || typeof updater !== 'function') return;
      const target = markets.value.find(item => String(item.marketid) === String(marketid));
      if (target) updater(target);
    };

    const isLockLoading = marketid => Boolean(lockLoading[marketid]);

    const toggleMarketLock = async row => {
      if (!row?.marketid || !row?.marketUrl || lockLoading[row.marketid]) return;
      const nextLocked = !row.locked;
      lockLoading[row.marketid] = true;
      try {
        const payload = buildMarketPayload(
          row.groupid,
          [row.marketUrl],
          nextLocked
        );
        await saveMarketList(payload);
        ElMessage.success(nextLocked ? '마켓을 잠금 처리했습니다.' : '마켓 잠금을 해제했습니다.');
        updateMarketRow(row.marketid, target => {
          target.locked = nextLocked;
        });
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '잠금 상태를 변경하지 못했습니다.');
      } finally {
        delete lockLoading[row.marketid];
      }
    };

    const handleSubmitMarketModal = async () => {
      const urls = marketModal.form.marketUrlsText
        .split('\n')
        .map(url => url.trim())
        .filter(Boolean);
      if (!urls.length) {
        ElMessage.warning('마켓 URL을 입력하세요.');
        return;
      }
      marketModal.submitting = true;
      try {
        const payload = buildMarketPayload(
          marketModal.form.groupid,
          urls
        );
        const { data } = await saveMarketList(payload);
        ElMessage.success(data?.message || '마켓 정보가 저장되었습니다.');
        closeMarketModal();
        loadMarkets();
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '마켓 저장에 실패했습니다.');
      } finally {
        marketModal.submitting = false;
      }
    };

    onMounted(async () => {
      await loadGroups();
      await loadMarkets();
    });

    watch(
      () => filters.groupid,
      () => scheduleSearch()
    );

    watch(
      () => filters.status,
      () => scheduleSearch()
    );

    watch(
      () => sortMode.value,
      value => {
        if (!sortModes.includes(value)) {
          sortMode.value = 'default';
          return;
        }
        scheduleSearch();
      }
    );

    return {
      Search,
      Edit,
      Lock,
      Unlock,
      Link,
      loading,
      markets,
      groupOptions,
      groupLabel,
      filters,
      pagination,
      marketStatuses,
      selectedMarkets,
      tableRef,
      marketModal,
      bulkModal,
      groupModal,
      emptyText,
      sortModeLabel,
      syncableCount,
      statusColorClass,
      formatNumber,
      formatRegistrationRate,
      registrationRateClass,
      formatDateOnly,
      openMarketUrl,
      handleSearch,
      handlePageChange,
      handleSizeChange,
      handleSelectionChange,
      handleDelete,
      openBulkModal,
      closeBulkModal,
      handleBulkLock,
      handleBulkRetrySync,
      handleBulkDelete,
      handleBulkOpenTabs,
      openMarketModal,
      openGroupModal,
      closeMarketModal,
      closeGroupModal,
      isGroupRowSaving,
      handleGroupCreate,
      activateGroupNameEdit,
      handleGroupNameBlur,
      handleGroupNameSubmit,
      handleGroupSyncToggle,
      handleDeleteGroup,
      handleRetryMarket,
      retryingMarketId,
      handleSubmitMarketModal,
      toggleMarketLock,
      isLockLoading,
      cycleSortMode
    };
  }
};
</script>

<style scoped>
.market-subscription {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--el-bg-color-page);
  overflow-y: auto;
  box-sizing: border-box;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  background: var(--el-bg-color);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  padding: var(--spacing-sm);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-xs);
}

.filter-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 200px;
}

.filter-item.url {
  flex: 1;
}

.filter-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  white-space: nowrap;
}

.toolbar-actions {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}

.toolbar-spacer {
  flex: 1;
  min-width: 16px;
}

.list-card {
  flex: 1;
  background: var(--el-bg-color);
  border-radius: var(--el-border-radius-base);
  border: 1px solid var(--el-border-color-lighter);
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.data-table {
  flex: 1;
}

.url-cell {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 0;
}

.group-cell {
  font-weight: var(--el-font-weight-medium);
  color: var(--el-text-color-primary);
}

.url-text {
  flex: 1;
  min-width: 0;
  color: var(--el-text-color-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-open-icon {
  cursor: pointer;
  color: var(--el-color-primary);
  transition: color 0.2s ease;
}

.url-open-icon:hover {
  color: var(--el-color-primary-dark-2);
}

.status-text {
  font-weight: var(--el-font-weight-medium);
  color: var(--el-text-color-primary);
}

.status-success {
  color: var(--el-color-success);
}

.status-error {
  color: var(--el-color-danger);
}

.status-warning {
  color: var(--el-color-warning);
}

.registration-rate {
  font-weight: var(--el-font-weight-medium);
}

.rate-success {
  color: var(--el-color-success);
}

.rate-warning {
  color: var(--el-color-warning);
}

.rate-danger {
  color: var(--el-color-danger);
}

.table-actions {
  display: flex;
  gap: var(--spacing-xs);
  justify-content: center;
}

.lock-button.el-button.is-plain {
  transition: none;
}

.lock-button.el-button.is-plain:not(.is-disabled):hover,
.lock-button.el-button.is-plain:not(.is-disabled):focus {
  background-color: transparent;
  border-color: currentColor;
  color: inherit;
  box-shadow: none;
}


.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-md);
}

.helper-text {
  margin: 0;
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.bulk-info-text {
  margin: 0 0 var(--spacing-sm) 0;
  color: var(--el-text-color-secondary);
  font-size: var(--el-font-size-small);
}

.bulk-actions-grid {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.bulk-actions-line {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}


.market-form-top {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-sm);
}

.market-form-group {
  flex: 1 1 220px;
  min-width: 220px;
}

.market-form-textarea textarea {
  font-family: var(--el-font-family);
}

.market-form {
  display: flex;
  flex-direction: column;
}

.group-create-row {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.group-edit-list {
  border: 1px solid var(--el-border-color-light);
  border-radius: var(--el-border-radius-base);
  overflow: hidden;
  max-height: 460px;
  overflow-y: auto;
}

.group-edit-header,
.group-edit-row {
  display: grid;
  grid-template-columns: 120px 1fr 150px 80px;
  align-items: center;
  padding: 6px 12px;
}

.group-edit-header {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
  background: var(--el-fill-color-light);
  border-bottom: 1px solid var(--el-border-color-light);
}

.group-edit-header span:nth-child(3),
.group-edit-header span:nth-child(4) {
  text-align: center;
}

.group-edit-row {
  border-bottom: 1px solid var(--el-border-color-extra-light);
}

.group-edit-row:last-child {
  border-bottom: none;
}

.group-row-id {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.group-row-name {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-height: 32px;
}

.group-row-input {
  width: 100%;
}

.group-row-text {
  flex: 1;
  color: var(--el-text-color-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-edit-icon {
  cursor: pointer;
  color: var(--el-text-color-secondary);
  transition: color 0.2s ease;
}

.group-edit-icon:hover {
  color: var(--el-color-primary);
}

.group-row-switch,
.group-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
}

.switch-label {
  font-size: var(--el-font-size-small);
  color: var(--el-text-color-secondary);
}

.group-empty-text {
  margin: 0;
  padding: var(--spacing-md) 0;
  text-align: center;
  color: var(--el-text-color-secondary);
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    min-width: unset;
  }

  .toolbar-actions {
    justify-content: flex-end;
  }

  .modal-panel {
    margin-bottom: var(--spacing-sm);
  }
}
</style>

